import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppModal from "./AppModal";

/**
 * PermissionHelpDialog — shown only when the browser has BLOCKED a required
 * permission (camera / microphone / location) and therefore refuses to
 * re-prompt. "Retry Permission" is useless in that state, so we guide the user
 * to re-enable access manually from the browser's address-bar / site-info menu
 * (same idea as Google Meet's "…is blocked from using your microphone and
 * camera" dialog).
 *
 * Rendered as a sibling of PreCallCheckModal's AppModal; AppModal is a full-screen
 * fixed overlay at z-1000, so this one — rendered after — stacks cleanly on top.
 *
 * Instructions are intentionally GENERIC (works across Chrome/Edge/Firefox/Safari,
 * desktop + mobile) and cover all three permissions at once — the browser's
 * site-info menu really does list them together. No page reload — closing
 * re-checks permissions, so an in-progress journey is never restarted.
 */

const ORANGE = "#EA7617"; // brand orange-150
const GREEN = "#3AA55D"; // brand green-150 → "allowed"
const GRAY_TRACK = "#D1D5DB";
const ICON_GRAY = "#9CA3AF";
const LINE_GRAY = "#E5E7EB";
const CURSOR = "#1F2937";
const DUR = "6s";
const KNOB_OFF = 241;
const KNOB_ON = 267;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

/**
 * One permission row's toggle inside the browser illustration. Slides the knob
 * grey → green ("allowed") and pulses a ripple, timed to when the cursor
 * arrives. `keyTimes` places the flip on the shared 6s master timeline.
 */
const Toggle = ({ cy, keyTimes, reduced }) => (
  <>
    <rect
      x="232"
      y={cy - 8.5}
      width="44"
      height="17"
      rx="8.5"
      fill={reduced ? GREEN : GRAY_TRACK}
    >
      {!reduced && (
        <animate
          attributeName="fill"
          values={`${GRAY_TRACK};${GRAY_TRACK};${GREEN};${GREEN};${GRAY_TRACK}`}
          keyTimes={keyTimes.track}
          dur={DUR}
          repeatCount="indefinite"
        />
      )}
    </rect>
    <circle cx={reduced ? KNOB_ON : KNOB_OFF} cy={cy} r="6.5" fill="#ffffff">
      {!reduced && (
        <animate
          attributeName="cx"
          values={`${KNOB_OFF};${KNOB_OFF};${KNOB_ON};${KNOB_ON};${KNOB_OFF}`}
          keyTimes={keyTimes.track}
          dur={DUR}
          repeatCount="indefinite"
        />
      )}
    </circle>
    {!reduced && (
      <circle cx="254" cy={cy} r="2" fill={GREEN} opacity="0">
        <animate
          attributeName="opacity"
          values="0;0;0.45;0;0"
          keyTimes={keyTimes.ripple}
          dur={DUR}
          repeatCount="indefinite"
        />
        <animate
          attributeName="r"
          values="2;2;2;18;2"
          keyTimes={keyTimes.ripple}
          dur={DUR}
          repeatCount="indefinite"
        />
      </circle>
    )}
  </>
);

/**
 * A stylised browser window. A cursor moves to the highlighted lock icon, then
 * flips the Camera, Microphone and Location toggles from grey (blocked) to green
 * (allowed), looping. Falls back to a static "allowed" frame when the user
 * prefers reduced motion. Purely decorative — the numbered steps carry the
 * instructions.
 */
const BrowserDemo = ({ reduced }) => (
  <svg
    viewBox="0 0 300 224"
    className="mx-auto w-full max-w-[300px]"
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    {/* browser card */}
    <rect
      x="8"
      y="8"
      width="284"
      height="204"
      rx="14"
      fill="#ffffff"
      stroke={LINE_GRAY}
      strokeWidth="1.5"
    />

    {/* window dots */}
    <circle cx="26" cy="26" r="3.5" fill="#FCA5A5" />
    <circle cx="38" cy="26" r="3.5" fill="#FDE047" />
    <circle cx="50" cy="26" r="3.5" fill="#86EFAC" />

    {/* lock highlight ring */}
    <circle cx="39" cy="53" r="12" fill={ORANGE} opacity={reduced ? 0.15 : 0.1}>
      {!reduced && (
        <animate
          attributeName="opacity"
          values="0.10;0.30;0.12;0.12"
          keyTimes="0;0.11;0.28;1"
          dur={DUR}
          repeatCount="indefinite"
        />
      )}
    </circle>

    {/* address bar + lock icon */}
    <rect x="22" y="40" width="256" height="26" rx="13" fill="#F3F4F6" />
    <path
      d="M35.5 51 v-3 a3.5 3.5 0 0 1 7 0 v3"
      fill="none"
      stroke={ORANGE}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <rect x="33.5" y="51" width="11" height="9" rx="1.8" fill={ORANGE} />
    <rect x="54" y="51" width="180" height="5" rx="2.5" fill={LINE_GRAY} />

    {/* --- Camera row (center y=100.5) --- */}
    <rect
      x="28"
      y="94"
      width="17"
      height="13"
      rx="3"
      fill="none"
      stroke={ICON_GRAY}
      strokeWidth="2"
    />
    <path
      d="M45 98 L52 94 L52 107 L45 103 Z"
      fill="none"
      stroke={ICON_GRAY}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <rect x="62" y="98" width="80" height="5" rx="2.5" fill={LINE_GRAY} />
    <Toggle
      cy={100.5}
      reduced={reduced}
      keyTimes={{ track: "0;0.32;0.36;0.97;1", ripple: "0;0.34;0.36;0.48;1" }}
    />

    {/* --- Microphone row (center y=139.5) --- */}
    <rect
      x="32"
      y="128"
      width="9"
      height="15"
      rx="4.5"
      fill="none"
      stroke={ICON_GRAY}
      strokeWidth="2"
    />
    <path
      d="M28 139 a8.5 8.5 0 0 0 17 0"
      fill="none"
      stroke={ICON_GRAY}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line x1="36.5" y1="147" x2="36.5" y2="152" stroke={ICON_GRAY} strokeWidth="2" />
    <line
      x1="31"
      y1="152"
      x2="42"
      y2="152"
      stroke={ICON_GRAY}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect x="62" y="137" width="100" height="5" rx="2.5" fill={LINE_GRAY} />
    <Toggle
      cy={139.5}
      reduced={reduced}
      keyTimes={{ track: "0;0.50;0.54;0.97;1", ripple: "0;0.52;0.54;0.66;1" }}
    />

    {/* --- Location row (center y=177.5) --- */}
    <path
      d="M36 168 c-4.4 0 -8 3.4 -8 7.5 c0 5.6 8 13.5 8 13.5 s8 -7.9 8 -13.5 c0 -4.1 -3.6 -7.5 -8 -7.5 Z"
      fill="none"
      stroke={ICON_GRAY}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="36" cy="175.5" r="2.6" fill="none" stroke={ICON_GRAY} strokeWidth="2" />
    <rect x="62" y="175" width="90" height="5" rx="2.5" fill={LINE_GRAY} />
    <Toggle
      cy={177.5}
      reduced={reduced}
      keyTimes={{ track: "0;0.66;0.70;0.97;1", ripple: "0;0.68;0.70;0.82;1" }}
    />

    {/* cursor */}
    {!reduced && (
      <g transform="translate(150,205)">
        <path
          d="M0 0 L0 17 L4.7 12.7 L7.5 18.7 L10.2 17.5 L7.4 11.7 L13 11.7 Z"
          fill={CURSOR}
          stroke="#ffffff"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <animateTransform
          attributeName="transform"
          type="translate"
          values="150,205; 40,46; 40,46; 250,88; 250,127; 250,165; 250,165; 150,205"
          keyTimes="0;0.11;0.20;0.32;0.50;0.66;0.80;1"
          dur={DUR}
          repeatCount="indefinite"
        />
      </g>
    )}
  </svg>
);

export default function PermissionHelpDialog({ open, onClose, selectedLang }) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  const steps = [
    t("Tap the lock or site-info icon near your browser's address bar."),
    t("Set Camera, Microphone and Location to Allow."),
    t("Return here — your access will update automatically."),
  ];

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t("Allow Required Permissions")}
      selectedLang={selectedLang}
      maxWidth="max-w-md"
      primaryText={t("Done")}
      onPrimary={onClose}
    >
      <p className="mb-0 text-sm text-gray-150 sm:text-base">
        {t(
          "Your browser is currently blocking access. Follow these steps to turn it on:",
        )}
      </p>

      <div className="mt-4 rounded-2xl bg-gray-50 p-4 sm:p-5">
        <BrowserDemo reduced={reduced} />
      </div>

      <ol className="mt-5 flex list-none flex-col gap-3 pl-0">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-150/10 text-xs font-bold text-orange-150">
              {i + 1}
            </span>
            <p className="mb-0 text-sm text-black-150 sm:text-base">{step}</p>
          </li>
        ))}
      </ol>
    </AppModal>
  );
}
